import { AlertTriangle, ChevronRight, Coins } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useBudgetLigne } from '@/api/hooks';
import type { AlerteExecution, DonneesExecution, GroupeExecution, IndicateursExecution, LigneExecution, PeriodeExecution, SituationRessources } from '@/api/types';
import { Kpi, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, compact, dateFr, montant, nombre, pct } from '@/lib/utils';

// ------------------------------------------------------------------ statuts (maquette : Alerte · Vigilance · Sous-exécution · Conforme)
export type NiveauStatut = 'ALERTE' | 'VIGILANCE' | 'SOUS_EXECUTION' | 'CONFORME';
export interface Constat {
  niveau: NiveauStatut;
  regle: string;
  detail: string;
}
export const LIBELLE_NIVEAU: Record<NiveauStatut, string> = { ALERTE: 'Alerte', VIGILANCE: 'Vigilance', SOUS_EXECUTION: 'Sous-exécution', CONFORME: 'Conforme' };
const ORDRE: Record<NiveauStatut, number> = { ALERTE: 0, VIGILANCE: 1, SOUS_EXECUTION: 2, CONFORME: 3 };
type Seuils = DonneesExecution['seuils'];
const r2 = (n: number): number => Math.round(n * 100) / 100;
const ratio = (num: number, den: number): number | null => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);

/** Règles d'alerte du moteur des rapports, recalculées à l'écran pour un agrégat (rubrique × direction). */
export function alertesCalculees(i: IndicateursExecution, periode: PeriodeExecution, seuils: Seuils, nonPayesAnciens: number): AlerteExecution[] {
  const a: AlerteExecution[] = [];
  if (i.engage > i.budgetRevise + 0.5) a.push({ code: 'DEPASSEMENT', detail: `engagé ${montant(i.engage)} pour un budget de ${montant(i.budgetRevise)}` });
  if (i.prevuPeriode > 0 && periode.fractionEcoulee >= 0.25 && i.realisePrevu !== null) {
    if (i.realisePrevu < seuils.retard) a.push({ code: 'RETARD', detail: `réalisé ${pct(i.realisePrevu)} du prévu de la période (seuil ${seuils.retard} %)` });
    else if (i.realisePrevu > seuils.avance) a.push({ code: 'AVANCE', detail: `réalisé ${pct(i.realisePrevu)} du prévu de la période (seuil ${seuils.avance} %)` });
  }
  if (nonPayesAnciens > 0) a.push({ code: 'NON_PAYE_ANCIEN', detail: `${nonPayesAnciens} engagement(s) non payé(s) depuis plus de ${seuils.ancienneteJours} jours` });
  return a;
}

/** Constats affichés : alertes du moteur (dépassement, surconsommation, sous-exécution, retard de paiement) et deux vigilances calculées à l'écran. */
export function constats(i: IndicateursExecution & { alertes: AlerteExecution[] }): Constat[] {
  const out: Constat[] = [];
  for (const a of i.alertes) {
    if (a.code === 'DEPASSEMENT') out.push({ niveau: 'ALERTE', regle: 'Dépassement', detail: `Engagé ${pct(i.tauxEngagement, 0)} du budget` });
    else if (a.code === 'AVANCE') out.push({ niveau: 'ALERTE', regle: 'Surconsommation', detail: `${pct(i.realisePrevu, 0)} du prévu à date` });
    else if (a.code === 'RETARD') out.push({ niveau: 'SOUS_EXECUTION', regle: 'Sous-exécution', detail: `${pct(i.realisePrevu, 0)} du prévu à date` });
    else if (a.code === 'NON_PAYE_ANCIEN') out.push({ niveau: 'VIGILANCE', regle: 'Retard de paiement', detail: a.detail });
  }
  const codes = new Set(i.alertes.map((a) => a.code));
  if (!codes.has('DEPASSEMENT') && i.tauxEngagement !== null && i.tauxEngagement >= 90) out.push({ niveau: 'VIGILANCE', regle: 'Crédit presque épuisé', detail: `Engagé ${pct(i.tauxEngagement, 0)} du budget` });
  if (!codes.has('AVANCE') && !codes.has('RETARD') && i.prevuPeriode > 0 && i.realisePrevu !== null && i.realisePrevu > 100) out.push({ niveau: 'VIGILANCE', regle: 'Rythme élevé', detail: `${pct(i.realisePrevu, 0)} du prévu à date` });
  return out.sort((a, b) => ORDRE[a.niveau] - ORDRE[b.niveau]);
}

export const niveauDe = (c: Constat[]): NiveauStatut => c.reduce<NiveauStatut>((n, x) => (ORDRE[x.niveau] < ORDRE[n] ? x.niveau : n), 'CONFORME');

/** Constats de toutes les lignes du périmètre, les plus graves d'abord puis par budget décroissant. */
export function constatsDesLignes(d: DonneesExecution): Array<{ ligne: LigneExecution; constat: Constat }> {
  return d.lignes
    .flatMap((ligne) => constats(ligne).map((constat) => ({ ligne, constat })))
    .sort((a, b) => ORDRE[a.constat.niveau] - ORDRE[b.constat.niveau] || b.ligne.budgetRevise - a.ligne.budgetRevise);
}

const CLASSES_NIVEAU: Record<NiveauStatut, string> = {
  ALERTE: 'border-red-200 bg-red-50 text-red-800',
  VIGILANCE: 'border-amber-200 bg-amber-50 text-amber-800',
  SOUS_EXECUTION: 'border-sky-200 bg-sky-50 text-sky-800',
  CONFORME: 'border-emerald-200 bg-emerald-50 text-emerald-800',
};

export function StatutExecution({ niveau, petit }: { niveau: NiveauStatut; petit?: boolean }) {
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap font-medium', CLASSES_NIVEAU[niveau], petit && 'px-1.5 text-[11px]')}>
      {LIBELLE_NIVEAU[niveau]}
    </Badge>
  );
}

// ------------------------------------------------------------------ briques
/** Indicateur clé : valeur abrégée, montant exact en infobulle, détail et note. */
export function Tuile({ label, valeur, unite, titre, detail, note, accent }: { label: string; valeur: string; unite?: string; titre?: string; detail?: ReactNode; note?: ReactNode; accent?: 'ok' | 'alerte' | 'critique' }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('num mt-1 whitespace-nowrap text-2xl font-semibold leading-tight', accent === 'alerte' && 'text-amber-700', accent === 'critique' && 'text-red-700', accent === 'ok' && 'text-emerald-700')} title={titre}>
        {valeur}
        {unite && <span className="ml-1 text-sm font-medium text-muted-foreground">{unite}</span>}
      </div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
      {note && <div className="mt-1 text-xs">{note}</div>}
    </div>
  );
}

/** Barre « réalisé / prévu » des tableaux : rouge au-delà de 110 %, bleu sous 70 %. */
export function BarreRealise({ v }: { v: number | null }) {
  if (v === null) return <span className="text-xs text-muted-foreground">—</span>;
  const couleur = v > 110 ? 'bg-red-500' : v < 70 ? 'bg-sky-500' : 'bg-primary';
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded bg-muted">
        <div className={cn('h-1.5 rounded', couleur)} style={{ width: `${Math.min(100, Math.max(0, v))}%` }} />
      </div>
      <span className="num w-10 text-right text-xs">{pct(v, 0)}</span>
    </div>
  );
}

/** Part du temps écoulé sur l'exercice à la date de situation (0 à 100). */
function tempsEcoule(d: DonneesExecution): number {
  const an = Number(d.dateSituation.slice(0, 4));
  if (d.exercice < an) return 100;
  if (d.exercice > an) return 0;
  const jour = (Date.parse(`${d.dateSituation}T00:00:00Z`) - Date.UTC(d.exercice, 0, 1)) / 86_400_000 + 1;
  const total = (Date.UTC(d.exercice + 1, 0, 1) - Date.UTC(d.exercice, 0, 1)) / 86_400_000;
  return Math.round((jour / total) * 100);
}

/** Les six indicateurs clés de la maquette. */
export function IndicateursCles({ d, nombreAlertes }: { d: DonneesExecution; nombreAlertes: number }) {
  const c = d.cles;
  const cumul = d.periode.type === 'CUMUL';
  const virements = r2(c.budgetRevise - c.budgetInitial);
  const ecoule = tempsEcoule(d);
  const evolution = c.payeN1Periode !== null && c.payeN1Periode > 0 ? ((c.payePeriode - c.payeN1Periode) / c.payeN1Periode) * 100 : null;
  const partNonPaye = ratio(c.engageNonPaye, c.engage);
  const partDisponible = ratio(c.soldeDisponible, c.budgetRevise);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Tuile label="Budget prévisionnel" valeur={compact(c.budgetRevise, '')} unite="USD" titre={montant(c.budgetRevise)} detail={virements !== 0 ? `Initial ${compact(c.budgetInitial)} · virements ${virements > 0 ? '+' : '−'}${compact(Math.abs(virements))}` : `Initial ${compact(c.budgetInitial)} · aucun virement net`} note={<span className="text-muted-foreground">{c.lignes} ligne(s)</span>} />
      <Tuile label="Engagé · cumul" valeur={compact(c.engage, '')} unite="USD" titre={montant(c.engage)} detail={`Taux d'engagement ${pct(c.tauxEngagement)}`} note={!cumul && <span className="text-muted-foreground">Période : {compact(c.engagePeriode)}</span>} />
      <Tuile
        label="Payé · cumul"
        valeur={compact(c.paye, '')}
        unite="USD"
        titre={montant(c.paye)}
        detail={c.prevuCumul > 0 ? `${pct(c.realisePrevuCumul, 0)} du prévu à date` : 'Aucun prévu au plan mensuel'}
        note={
          evolution !== null ? (
            <span className={cn('font-medium', evolution < 0 ? 'text-red-700' : 'text-emerald-700')}>
              {evolution > 0 ? '+' : ''}
              {pct(evolution)} vs {d.comparaisonLibelle?.toLowerCase()}
            </span>
          ) : !cumul ? (
            <span className="text-muted-foreground">Période : {compact(c.payePeriode)}</span>
          ) : undefined
        }
      />
      <Tuile label="Taux d'exécution" valeur={pct(c.tauxExecution)} detail={`Payé cumulé / budget · temps écoulé ${pct(ecoule, 0)}`} accent={c.tauxExecution !== null && c.tauxExecution < ecoule - 20 ? 'alerte' : undefined} />
      <Tuile
        label="Engagé non payé · à date"
        valeur={compact(c.engageNonPaye, '')}
        unite="USD"
        titre={montant(c.engageNonPaye)}
        detail={`${pct(partNonPaye, 0)} de l'engagé · en attente trésorerie`}
        note={d.nonPayesTotal.anciens > 0 ? <span className="font-medium text-amber-700">{compact(d.nonPayesTotal.montantAnciens)} depuis plus de {d.seuils.ancienneteJours} jours</span> : <span className="text-muted-foreground">Aucun retard de paiement</span>}
      />
      <Tuile
        label="Solde disponible"
        valeur={compact(c.soldeDisponible, '')}
        unite="USD"
        titre={montant(c.soldeDisponible)}
        detail={`Budget prévisionnel − engagé · ${pct(partDisponible, 0)} du budget`}
        accent={c.soldeDisponible < 0 ? 'critique' : undefined}
        note={nombreAlertes > 0 ? <Badge variant="destructive">{nombreAlertes} alerte(s)</Badge> : <span className="text-emerald-700">Aucune alerte</span>}
      />
    </div>
  );
}

const axeCompact = (v: number): string => compact(v, '');

/** Trajectoire cumulée de l'exercice : prévu, engagé et payé cumulés, payé N-1 en comparaison, budget en repère. */
export function TrajectoireCumulee({ d }: { d: DonneesExecution }) {
  const an = Number(d.dateSituation.slice(0, 4));
  const moisSituation = d.exercice < an ? 12 : d.exercice > an ? 0 : Number(d.dateSituation.slice(5, 7));
  let engageCumul = 0;
  const data = d.mensuel.map((m) => {
    engageCumul = r2(engageCumul + m.engage);
    const ecoule = m.mois <= moisSituation;
    return { nom: m.libelle, Prévu: m.prevuCumul, Engagé: ecoule ? engageCumul : null, Payé: ecoule ? m.payeCumul : null, ...(m.payeN1Cumul !== null ? { 'Payé N-1': m.payeN1Cumul } : {}) };
  });
  const n1 = d.mensuel.some((m) => m.payeN1Cumul !== null);
  if (!d.cles.lignes) return <Vide message="Aucune ligne budgétaire sur ce périmètre." />;
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ left: 8, right: 16, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="nom" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={axeCompact} width={56} />
          <Tooltip formatter={(v) => montant(Number(v))} />
          <Legend />
          <ReferenceLine y={d.cles.budgetRevise} stroke="var(--chart-4)" strokeDasharray="4 4" label={{ value: `Budget ${compact(d.cles.budgetRevise, '')}`, position: 'insideTopLeft', fontSize: 11, fill: 'var(--chart-4)' }} />
          <Line type="monotone" dataKey="Prévu" stroke="#94a3b8" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Engagé" stroke="var(--chart-3)" strokeWidth={2} dot={false} connectNulls={false} />
          <Line type="monotone" dataKey="Payé" stroke="var(--chart-1)" strokeWidth={2.5} dot={false} connectNulls={false} />
          {n1 && <Line type="monotone" dataKey="Payé N-1" stroke="var(--chart-5)" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Prévu et payé par mois (vue d'une direction) ; les mois de la période sélectionnée sont surlignés. */
export function PrevuPayeParMois({ d }: { d: DonneesExecution }) {
  const data = d.mensuel.map((m) => ({ nom: m.libelle, Prévu: m.prevu, Payé: m.paye, dans: m.mois >= d.periode.moisDebut && m.mois <= d.periode.moisFin, ecart: m.prevu > 0 ? m.paye / m.prevu : null }));
  if (!d.cles.lignes) return <Vide message="Aucune ligne budgétaire sur ce périmètre." />;
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="nom" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={axeCompact} width={56} />
          <Tooltip formatter={(v) => montant(Number(v))} />
          <Legend />
          <Bar dataKey="Prévu" fill="#cbd5e1" radius={[3, 3, 0, 0]}>
            {data.map((x, i) => (
              <Cell key={i} fillOpacity={x.dans ? 1 : 0.5} />
            ))}
          </Bar>
          <Bar dataKey="Payé" fill="var(--chart-1)" radius={[3, 3, 0, 0]}>
            {data.map((x, i) => (
              <Cell key={i} fill={x.ecart !== null && x.ecart > 1.1 ? 'var(--chart-4)' : x.ecart !== null && x.ecart < 0.7 && x.Payé > 0 ? 'var(--chart-2)' : 'var(--chart-1)'} fillOpacity={x.dans ? 1 : 0.5} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Exécution par grande masse : barre payé / budget, trait noir au prévu à date. */
export function ExecutionParMasse({ d }: { d: DonneesExecution }) {
  if (!d.masses.length) return <Vide message="Aucune rubrique." />;
  return (
    <div className="space-y-3">
      {d.masses.map((m) => {
        const taux = m.tauxExecution ?? 0;
        const prevu = ratio(m.prevuPeriode, m.budgetRevise);
        return (
          <div key={m.code}>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="truncate font-medium">{m.libelle}</span>
              <span className="num shrink-0 text-xs text-muted-foreground" title={`${montant(m.paye)} payés sur ${montant(m.budgetRevise)}`}>
                {compact(m.paye, '')} / {compact(m.budgetRevise, '')}
              </span>
              <span className="num w-14 shrink-0 text-right font-semibold">{pct(taux)}</span>
            </div>
            <div className="relative mt-1 h-2.5 rounded-full bg-muted">
              <div className="h-2.5 rounded-full bg-primary" style={{ width: `${Math.min(100, taux)}%` }} />
              {prevu !== null && <div className="absolute -top-0.5 h-3.5 w-0.5 bg-foreground" style={{ left: `${Math.min(100, prevu)}%` }} title={`Prévu à date : ${pct(prevu, 0)} du budget`} />}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-muted-foreground">Barre : payé / budget prévisionnel · trait noir : prévu à date</div>
      {(d.axes ?? []).filter((a) => a.code !== '—').length > 0 && (
        <div className="border-t pt-3">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Par axe stratégique (PTBA)</div>
          {(d.axes ?? []).map((a) => (
            <div key={a.code} className="mb-1.5">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">{a.libelle}</span>
                <span className="num w-14 shrink-0 text-right font-semibold">{pct(a.tauxExecution ?? 0)}</span>
              </div>
              <div className="num text-xs text-muted-foreground">
                {a.lignes} sous-activité(s) · {compact(a.paye, '')} / {compact(a.budgetRevise, '')}
              </div>
            </div>
          ))}
        </div>
      )}
      {(d.natures ?? []).length > 0 && (
        <div className="border-t pt-3">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Budget par nature de dépense (PTBA)</div>
          {(d.natures ?? []).map((n) => (
            <div key={n.code} className="mb-1.5">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium" title={n.libelle}>
                  {n.code} · {n.libelle}
                </span>
                <span className="num w-12 shrink-0 text-right font-semibold">{pct(n.part, 0)}</span>
              </div>
              <div className="num text-xs text-muted-foreground">
                {n.lignes} ligne(s) de dépense · {compact(n.budget, '')}
              </div>
            </div>
          ))}
          <div className="text-xs text-muted-foreground">Budget des lignes de dépense de la version approuvée ; l'exécution est suivie par sous-activité.</div>
        </div>
      )}
    </div>
  );
}

/** Chaîne de la dépense : prévu à date → engagé → payé → non payé. */
export function ChaineDepense({ d }: { d: DonneesExecution }) {
  const cumul = d.periode.type === 'CUMUL';
  const c = d.cles;
  const base = cumul ? c.prevuCumul : c.prevuPeriode;
  const engage = cumul ? c.engage : c.engagePeriode;
  const paye = cumul ? c.paye : c.payePeriode;
  const nonPaye = cumul ? c.engageNonPaye : Math.max(0, r2(engage - paye));
  const part = (n: number, den: number) => (den > 0 ? pct((n / den) * 100, 0) : '—');
  const etapes = [
    { l: cumul ? 'Prévu à date' : 'Prévu de la période', v: base, s: 'plan mensuel' },
    { l: 'Engagé', v: engage, s: `${part(engage, base)} du prévu` },
    { l: 'Payé', v: paye, s: `${part(paye, base)} du prévu` },
    { l: 'Non payé', v: nonPaye, s: `${part(nonPaye, engage)} de l'engagé` },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {etapes.map((e, k) => (
          <div key={e.l} className="relative rounded-md border p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{e.l}</div>
            <div className="num mt-1 text-lg font-semibold" title={montant(e.v)}>
              {compact(e.v, '')}
            </div>
            <div className="text-xs text-muted-foreground">{e.s}</div>
            {k < etapes.length - 1 && <ChevronRight className="absolute -right-2.5 top-1/2 hidden size-4 -translate-y-1/2 text-muted-foreground sm:block" />}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Montants en M ou k USD. Le non payé correspond aux formulaires validés et comptabilisés, pas encore payés par la trésorerie : s'il grossit ou vieillit, c'est un signal de tension de trésorerie.</p>
    </div>
  );
}

// ------------------------------------------------------------------ tableaux
function agreger(lignes: LigneExecution[]): IndicateursExecution {
  const somme = (f: (l: LigneExecution) => number) => r2(lignes.reduce((s, l) => s + f(l), 0));
  const budgetRevise = somme((l) => l.budgetRevise);
  const engage = somme((l) => l.engage);
  const paye = somme((l) => l.paye);
  const prevuPeriode = somme((l) => l.prevuPeriode);
  const payePeriode = somme((l) => l.payePeriode);
  const n1 = lignes.some((l) => l.payeN1Periode !== null) ? somme((l) => l.payeN1Periode ?? 0) : null;
  return { budgetInitial: somme((l) => l.budgetInitial), budgetRevise, engage, liquide: somme((l) => l.liquide), paye, tauxEngagement: ratio(engage, budgetRevise), tauxExecution: ratio(paye, budgetRevise), engageNonPaye: r2(engage - paye), soldeDisponible: r2(budgetRevise - engage), prevuPeriode, engagePeriode: somme((l) => l.engagePeriode), payePeriode, realisePrevu: ratio(payePeriode, prevuPeriode), payeN1Periode: n1 };
}

const cellules = (i: IndicateursExecution, negatif?: boolean) => (
  <>
    <TableCell className="num text-right">{nombre(i.budgetRevise)}</TableCell>
    <TableCell className="num hidden text-right md:table-cell">{nombre(i.prevuPeriode)}</TableCell>
    <TableCell className="num text-right">{nombre(i.payePeriode)}</TableCell>
    <TableCell className="hidden lg:table-cell">
      <BarreRealise v={i.realisePrevu} />
    </TableCell>
    <TableCell className="num text-right">{pct(i.tauxExecution)}</TableCell>
    <TableCell className={cn('num hidden text-right sm:table-cell', (negatif || i.soldeDisponible < 0) && 'font-semibold text-red-700')}>{nombre(i.soldeDisponible)}</TableCell>
  </>
);

const EnteteRubriques = () => (
  <TableHeader>
    <TableRow>
      <TableHead>Rubrique</TableHead>
      <TableHead className="text-right">Budget prévisionnel</TableHead>
      <TableHead className="hidden text-right md:table-cell">Prévu période</TableHead>
      <TableHead className="text-right">Payé période</TableHead>
      <TableHead className="hidden text-right lg:table-cell">Réalisé / prévu</TableHead>
      <TableHead className="text-right">Taux exéc.</TableHead>
      <TableHead className="hidden text-right sm:table-cell">Solde dispo.</TableHead>
      <TableHead>Statut</TableHead>
    </TableRow>
  </TableHeader>
);

/** Exécution par rubrique (grande masse) puis par direction ; un clic sur une direction ouvre son détail. */
export function TableRubriques({ d, onStructure }: { d: DonneesExecution; onStructure: (code: string) => void }) {
  if (!d.lignes.length) return <Vide message="Aucune ligne budgétaire sur ce périmètre." />;
  const parMasse = new Map<string, LigneExecution[]>();
  for (const l of d.lignes) parMasse.set(l.programme, [...(parMasse.get(l.programme) ?? []), l]);
  const masses = [...parMasse.entries()].sort((a, b) => agreger(b[1]).budgetRevise - agreger(a[1]).budgetRevise);
  return (
    <Table>
      <EnteteRubriques />
      <TableBody>
        {masses.map(([masse, lignes]) => {
          const groupe: GroupeExecution | undefined = d.masses.find((m) => m.code === masse);
          const iMasse = agreger(lignes);
          const parStructure = new Map<string, LigneExecution[]>();
          for (const l of lignes) parStructure.set(l.structureCode, [...(parStructure.get(l.structureCode) ?? []), l]);
          const structures = [...parStructure.entries()].sort((a, b) => agreger(b[1]).budgetRevise - agreger(a[1]).budgetRevise);
          return [
            <TableRow key={masse} className="bg-muted/50 font-semibold">
              <TableCell className="uppercase tracking-wide">
                {masse}
                {groupe && groupe.nombreLignesEnAlerte > 0 && <span className="ml-2 text-xs font-normal text-muted-foreground">{groupe.nombreLignesEnAlerte} ligne(s) en alerte</span>}
              </TableCell>
              {cellules(iMasse)}
              <TableCell />
            </TableRow>,
            ...structures.map(([code, ls]) => {
              const i = agreger(ls);
              const anciens = ls.reduce((s, l) => s + l.nonPayesAnciens, 0);
              const niveau = niveauDe(constats({ ...i, alertes: alertesCalculees(i, d.periode, d.seuils, anciens) }));
              const enAlerte = ls.filter((l) => l.alertes.length).length;
              return (
                <TableRow key={`${masse}-${code}`} className="cursor-pointer hover:bg-accent/60" onClick={() => onStructure(code)} title="Ouvrir le détail de cette direction">
                  <TableCell>
                    <div className="flex items-center gap-1 font-medium">
                      {ls[0]!.structureLibelle}
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {code} · {ls.length} ligne(s){enAlerte ? ` · ${enAlerte} en alerte` : ''}
                    </div>
                  </TableCell>
                  {cellules(i)}
                  <TableCell>
                    <StatutExecution niveau={niveau} petit />
                  </TableCell>
                </TableRow>
              );
            }),
          ];
        })}
        <TableRow className="border-t-2 font-semibold">
          <TableCell>Total {d.perimetre.code === 'FONDS' ? 'FONAREV' : d.perimetre.libelle}</TableCell>
          {cellules(d.cles)}
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}

const cellulesLigne = (i: IndicateursExecution) => (
  <>
    <TableCell className="num text-right">{nombre(i.budgetRevise)}</TableCell>
    <TableCell className="num text-right">{nombre(i.engage)}</TableCell>
    <TableCell className="num hidden text-right md:table-cell">{nombre(i.paye)}</TableCell>
    <TableCell className="num hidden text-right lg:table-cell">{nombre(i.engageNonPaye)}</TableCell>
    <TableCell className="hidden lg:table-cell">
      <BarreRealise v={i.realisePrevu} />
    </TableCell>
    <TableCell className="num text-right">{pct(i.tauxExecution, 0)}</TableCell>
    <TableCell className={cn('num hidden text-right sm:table-cell', i.soldeDisponible < 0 && 'font-semibold text-red-700')}>{nombre(i.soldeDisponible)}</TableCell>
  </>
);

/** Services et lignes d'une direction (ou lignes d'un service) ; un clic sur une ligne ouvre ses mouvements. */
export function TableServicesLignes({ d, onLigne }: { d: DonneesExecution; onLigne: (l: LigneExecution) => void }) {
  if (!d.lignes.length) return <Vide message="Aucune ligne budgétaire sur ce périmètre." />;
  const services = d.services ?? [];
  // Direction avec services : lignes par service ; sinon ventilation par activité (v1.1, § 7), les lignes étant les sous-activités.
  const activites = d.activites ?? [];
  const groupes: Array<{ groupe: GroupeExecution | null; lignes: LigneExecution[] }> = services.length ? services.map((g) => ({ groupe: g, lignes: d.lignes.filter((l) => (l.serviceCode || '—') === g.code) })) : activites.length > 1 ? activites.map((g) => ({ groupe: g, lignes: d.lignes.filter((l) => l.activiteCode === g.code) })) : [{ groupe: null, lignes: d.lignes }];
  const ligneRow = (l: LigneExecution) => (
    <TableRow key={l.id} className="cursor-pointer hover:bg-accent/60" onClick={() => onLigne(l)} title="Ouvrir les mouvements de la ligne">
      <TableCell>
        <div className="font-medium">{l.libelle}</div>
        <div className="text-xs text-muted-foreground">
          {l.code} · {l.activiteLibelle} · {l.programme} · {l.source}
        </div>
      </TableCell>
      {cellulesLigne(l)}
      <TableCell>
        <StatutExecution niveau={niveauDe(constats(l))} petit />
      </TableCell>
    </TableRow>
  );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{services.length ? 'Service / ligne' : activites.length > 1 ? 'Activité / sous-activité' : 'Ligne'}</TableHead>
          <TableHead className="text-right">Budget prévisionnel</TableHead>
          <TableHead className="text-right">Engagé</TableHead>
          <TableHead className="hidden text-right md:table-cell">Payé</TableHead>
          <TableHead className="hidden text-right lg:table-cell">Non payé</TableHead>
          <TableHead className="hidden text-right lg:table-cell">Réalisé / prévu</TableHead>
          <TableHead className="text-right">Taux exéc.</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Solde dispo.</TableHead>
          <TableHead>Statut</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groupes.map(({ groupe, lignes }) =>
          groupe
            ? [
                <TableRow key={`s-${groupe.code}`} className="bg-muted/50 font-semibold">
                  <TableCell className="uppercase tracking-wide">
                    {groupe.libelle}
                    <span className="ml-2 text-xs font-normal normal-case text-muted-foreground">{groupe.lignes} ligne(s)</span>
                  </TableCell>
                  {cellulesLigne(groupe)}
                  <TableCell>
                    <StatutExecution niveau={niveauDe(constats(groupe))} petit />
                  </TableCell>
                </TableRow>,
                ...lignes.map(ligneRow),
              ]
            : lignes.map(ligneRow),
        )}
        <TableRow className="border-t-2 font-semibold">
          <TableCell>Total {d.perimetre.libelle}</TableCell>
          {cellulesLigne(d.cles)}
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}

/** Points d'attention de la synthèse : les constats les plus graves, avec un accès à la liste complète. */
export function PointsAttention({ d, onToutVoir }: { d: DonneesExecution; onToutVoir: () => void }) {
  const tous = constatsDesLignes(d);
  const premiers = tous.slice(0, 8);
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div>
          <CardTitle>Points d'attention</CardTitle>
          <CardDescription>Générés par les règles d'alerte · {tous.length} au total</CardDescription>
        </div>
        {tous.length > 0 && (
          <Button variant="outline" size="sm" onClick={onToutVoir}>
            Tout voir
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!premiers.length ? (
          <Vide message="Aucune ligne budgétaire n'est en alerte sur la période." />
        ) : (
          <ul className="divide-y">
            {premiers.map(({ ligne, constat }, k) => (
              <li key={`${ligne.id}-${k}`} className="flex items-center gap-3 py-2">
                <StatutExecution niveau={constat.niveau} petit />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {constat.regle} · {ligne.libelle}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {ligne.structureLibelle} · {constat.detail}
                  </div>
                </div>
                <div className="num shrink-0 text-right text-sm" title={`${montant(ligne.paye)} payés sur ${montant(ligne.budgetRevise)}`}>
                  <span className="font-semibold">{compact(ligne.paye, '')}</span>
                  <span className="text-muted-foreground"> / {compact(ligne.budgetRevise, '')}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Lignes en alerte (onglet Alertes) : filtre par niveau, règle, ligne, rubrique, montants et constat. */
export function TableAlertes({ d }: { d: DonneesExecution }) {
  const [filtre, setFiltre] = useState<NiveauStatut | 'TOUTES'>('TOUTES');
  const tous = constatsDesLignes(d);
  const compte = (n: NiveauStatut) => tous.filter((x) => x.constat.niveau === n).length;
  const visibles = filtre === 'TOUTES' ? tous : tous.filter((x) => x.constat.niveau === filtre);
  const chips: Array<[NiveauStatut | 'TOUTES', string, number]> = [
    ['TOUTES', 'Toutes', tous.length],
    ['ALERTE', 'Alertes', compte('ALERTE')],
    ['VIGILANCE', 'Vigilance', compte('VIGILANCE')],
    ['SOUS_EXECUTION', 'Sous-exécution', compte('SOUS_EXECUTION')],
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lignes en alerte</CardTitle>
        <CardDescription>
          Évaluées sur {d.periode.libelle.toLowerCase()} · seuils : sous-exécution sous {d.seuils.retard} % du prévu, surconsommation au-delà de {d.seuils.avance} %, retard de paiement au-delà de {d.seuils.ancienneteJours} jours (réglables par la Direction financière dans Administration › Paramètres).
        </CardDescription>
        <div className="flex flex-wrap gap-2 pt-2">
          {chips.map(([v, l, n]) => (
            <Button key={v} size="sm" variant={filtre === v ? 'default' : 'outline'} onClick={() => setFiltre(v)}>
              {l} ({n})
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {!visibles.length ? (
          <Vide message="Aucune ligne dans cette catégorie." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Niveau</TableHead>
                <TableHead>Règle</TableHead>
                <TableHead>Ligne</TableHead>
                <TableHead className="hidden md:table-cell">Rubrique</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="hidden text-right lg:table-cell">Prévu à date</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Engagé</TableHead>
                <TableHead className="text-right">Payé</TableHead>
                <TableHead className="hidden lg:table-cell">Constat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map(({ ligne, constat }, k) => (
                <TableRow key={`${ligne.id}-${k}`}>
                  <TableCell>
                    <StatutExecution niveau={constat.niveau} petit />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{constat.regle}</TableCell>
                  <TableCell>
                    <div className="font-medium">{ligne.libelle}</div>
                    <div className="text-xs text-muted-foreground">{ligne.code}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {ligne.structureLibelle}
                    <div className="text-xs text-muted-foreground">{ligne.programme}</div>
                  </TableCell>
                  <TableCell className="num text-right">{nombre(ligne.budgetRevise)}</TableCell>
                  <TableCell className="num hidden text-right lg:table-cell">{nombre(ligne.prevuPeriode)}</TableCell>
                  <TableCell className="num hidden text-right sm:table-cell">{nombre(ligne.engage)}</TableCell>
                  <TableCell className="num text-right">{nombre(ligne.paye)}</TableCell>
                  <TableCell className="hidden text-xs lg:table-cell">{constat.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** Ressources et quotes-parts du Fonds (même bloc que le tableau de bord). */
export function BlocRessources({ r }: { r: SituationRessources }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Ressources et quotes-parts {r.exercice}
          {r.alertes.length > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="size-3" /> {r.alertes.length} quote(s)-part(s) impayée(s) à la clôture
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Encaissé {montant(r.totaux.encaisse)} pour {montant(r.totaux.prevu)} prévus à date ({pct(r.totaux.tauxRealisation)}) · couverture des paiements {pct(r.totaux.tauxCouverture)}.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={r.trimestres.map((t) => ({ nom: t.libelle, Prévu: t.prevu, Encaissé: t.encaisse }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nom" />
              <YAxis tickFormatter={axeCompact} width={56} />
              <Tooltip formatter={(v) => montant(Number(v ?? 0))} />
              <Legend />
              <Bar dataKey="Prévu" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Encaissé" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3 text-sm">
          {(['CIA_VAR', 'ECOSYSTEME'] as const).map((b) => {
            const q = r.quotesParts[b];
            return (
              <div key={b} className="rounded-md border p-3">
                <div className="font-medium">
                  {q.libelle} <span className="text-xs text-muted-foreground">· {q.taux} %</span>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Due</div>
                    <div className="num">{nombre(q.due)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Payée</div>
                    <div className="num">{nombre(q.payee)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Restant dû</div>
                    <div className={q.restantDu > 0.5 ? 'num font-semibold text-orange-700' : 'num'}>{nombre(q.restantDu)}</div>
                  </div>
                </div>
              </div>
            );
          })}
          <Button variant="outline" size="sm" asChild>
            <Link to="/ressources">
              <Coins className="size-4" /> Ressources et quotes-parts
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ détail d'une ligne
const TYPE_MVT: Record<string, string> = { ENGAGEMENT: 'Engagement', LIQUIDATION: 'Liquidation', PAIEMENT: 'Paiement', VIREMENT: 'Virement', RETOUR: 'Retour' };

/** Tiroir d'une ligne : situation complète et mouvements (engagements, liquidations, paiements, virements, retours). */
export function DetailLigne({ ligne, onClose }: { ligne: { id: string; code: string; libelle: string } | null; onClose: () => void }) {
  const { data, isLoading } = useBudgetLigne(ligne?.id);
  return (
    <Sheet open={Boolean(ligne)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            {ligne?.code} · {ligne?.libelle}
          </SheetTitle>
          <SheetDescription>
            {data ? `${data.structureLibelle} · ${data.programme} · ${data.source}` : 'Situation de la ligne et mouvements.'}
            {data?.activiteLibelle && ` · activité ${data.activiteCode ?? ''} ${data.activiteLibelle}`}
          </SheetDescription>
        </SheetHeader>
        {ligne && (
          <div className="space-y-4 px-4 pb-6">
            {isLoading || !data ? (
              <div className="text-sm text-muted-foreground">Chargement…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Kpi label="Budget prévisionnel" valeur={montant(data.budgetActualise)} detail={data.budgetActualise !== data.budgetInitial ? `initial ${montant(data.budgetInitial)}` : 'aucun virement'} />
                  <Kpi label="Engagé" valeur={montant(data.engage)} detail={pct(data.tauxEngagement)} />
                  <Kpi label="Liquidé" valeur={montant(data.liquide)} />
                  <Kpi label="Payé" valeur={montant(data.paye)} detail={pct(data.tauxExecution)} />
                  <Kpi label="Disponible" valeur={montant(data.disponible)} detail="budget prévisionnel − engagé" accent={data.disponible < 0 ? 'critique' : undefined} />
                  <Kpi label="Disponible après circuit" valeur={montant(data.disponiblePrevisionnel)} detail={`${montant(data.enCircuit)} en circuit`} accent={data.disponiblePrevisionnel < 0 ? 'alerte' : undefined} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {data.reserve && <Badge variant="secondary">Provision pour imprévus · fermée à l’initiation, mobilisable par virement</Badge>}
                  {!data.actif && <Badge variant="destructive">Retirée de la version approuvée du PTBA · aucune initiation</Badge>}
                  {data.alertes.map((a) => (
                    <Badge key={a} variant="destructive">
                      {a}
                    </Badge>
                  ))}
                </div>
                {(data.version || data.programmation.axeCode || data.programmation.kpi || data.programmation.responsableLibelle) && (
                  <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-medium">
                        Programmation (PTBA)
                        {!data.reserve && (
                          <Link to={`/plan/${data.id}`} className="ml-2 text-xs font-normal text-primary hover:underline">
                            Fiche de suivi
                          </Link>
                        )}
                      </div>
                      {data.version && (
                        <Link to={`/budget/versions?exercice=${data.exercice}&version=${data.version.id}`} className="text-xs text-primary hover:underline">
                          Version {data.version.numero} · {data.version.statut === 'APPROUVE' ? 'approuvée' : data.version.statut === 'REMPLACE' ? 'remplacée' : data.version.statut.toLowerCase()}
                        </Link>
                      )}
                    </div>
                    <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1 text-xs">
                      {data.programmation.axeCode && (
                        <>
                          <dt className="text-muted-foreground">Axe · objectif</dt>
                          <dd>
                            {data.programmation.axeCode}
                            {data.programmation.objectifCode && ` · ${data.programmation.objectifCode}`}
                            {data.programmation.objectifLibelle && ` ${data.programmation.objectifLibelle}`}
                          </dd>
                        </>
                      )}
                      {data.programmation.resultatAttendu && (
                        <>
                          <dt className="text-muted-foreground">Résultat attendu</dt>
                          <dd>{data.programmation.resultatAttendu}</dd>
                        </>
                      )}
                      {data.programmation.kpi && (
                        <>
                          <dt className="text-muted-foreground">Indicateur · cible</dt>
                          <dd>
                            {data.programmation.kpi}
                            {data.programmation.cibleValeur !== null ? ` · ${nombre(data.programmation.cibleValeur, 0)} ${data.programmation.cibleUnite ?? ''}` : data.programmation.cibleTexte ? ` · ${data.programmation.cibleTexte}` : ''}
                          </dd>
                        </>
                      )}
                      {data.programmation.livrable && (
                        <>
                          <dt className="text-muted-foreground">Livrable</dt>
                          <dd>{data.programmation.livrable}</dd>
                        </>
                      )}
                      {(data.programmation.lieux.length > 0 || data.programmation.lieuPrecision) && (
                        <>
                          <dt className="text-muted-foreground">Lieux</dt>
                          <dd>{[data.programmation.lieux.join(', '), data.programmation.lieuPrecision].filter(Boolean).join(' · ')}</dd>
                        </>
                      )}
                      {data.programmation.responsableLibelle && (
                        <>
                          <dt className="text-muted-foreground">Responsable</dt>
                          <dd>
                            {data.programmation.responsableLibelle}
                            {data.programmation.partenaires && <span className="text-muted-foreground"> · partenaires : {data.programmation.partenaires}</span>}
                          </dd>
                        </>
                      )}
                      {(data.programmation.debutPrevu || data.programmation.finPrevue) && (
                        <>
                          <dt className="text-muted-foreground">Période prévue</dt>
                          <dd>
                            {dateFr(data.programmation.debutPrevu)} → {dateFr(data.programmation.finPrevue)}
                          </dd>
                        </>
                      )}
                    </dl>
                  </div>
                )}
                {data.lignesDepense.length > 0 && (
                  <div>
                    <div className="mb-2 text-sm font-medium">Lignes de dépense du PTBA</div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Identifiant</TableHead>
                          <TableHead>Nature · catégorie</TableHead>
                          <TableHead className="text-right">Qté × fréq. × coût</TableHead>
                          <TableHead className="text-right">Budget</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.lignesDepense.map((d) => (
                          <TableRow key={d.id} className={cn(!d.actif && 'text-muted-foreground line-through')}>
                            <TableCell className="whitespace-nowrap font-mono text-xs">{d.code}</TableCell>
                            <TableCell>
                              {d.natureLibelle ?? d.natureCode}
                              {d.categorie && <div className="text-xs text-muted-foreground">{d.categorie}</div>}
                            </TableCell>
                            <TableCell className="num whitespace-nowrap text-right text-xs">
                              {nombre(d.quantite, 0)} × {nombre(d.frequence, 0)} × {nombre(d.coutUnitaire, 2)}
                            </TableCell>
                            <TableCell className="num text-right">{montant(d.budget, true)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <div>
                  <div className="mb-2 text-sm font-medium">Mouvements</div>
                  {!data.mouvements.length ? (
                    <Vide message="Aucun mouvement sur cette ligne." />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Libellé</TableHead>
                          <TableHead className="text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.mouvements.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell className="whitespace-nowrap">{dateFr(m.date)}</TableCell>
                            <TableCell>{TYPE_MVT[m.type] ?? m.type}</TableCell>
                            <TableCell>
                              {m.libelle}
                              {m.reference && <div className="text-xs text-muted-foreground">{m.reference}</div>}
                            </TableCell>
                            <TableCell className="num text-right">{montant(m.montant, true)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
